import React, { useEffect, useContext, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { getToken } from "../../utils/tokenUtils";
import { AuthContext } from "../../contexts/AuthContext";
import * as XLSX from 'xlsx';
import { getRoleBasedRoute } from '../../utils/routeUtils';
import { 
  User, 
  Calendar, 
  Clock, 
  Download, 
  RefreshCw,
  UserCheck,
  UserX,
  Coffee,
  FileSpreadsheet,
  ChevronDown
} from 'lucide-react';
import StatusBadge from "../../components/UI/StatusBadge";

const AttendancePage = () => {
  const { user, roles = [] } = useContext(AuthContext);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [defaultMonth, setDefaultMonth] = useState('');
  const token = getToken();
  const employeeId = attendanceData?.employee_id || '';

  const formatTo12Hour = (timeStr) => {
    if (!timeStr) return '-';
    const [hour, minute] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hour), parseInt(minute));
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };
  
  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://hrmspayroll.prepstripe.com/api/my-attendance', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      const data = response.data;
      setAttendanceData(data);
  
      // Auto-select current month
      const today = new Date();
      const currentMonthStr = `${today.toLocaleString('default', { month: 'long' })} ${today.getFullYear()}`;
      setDefaultMonth(currentMonthStr);

      const foundMonth = data.monthly_attendance?.find(entry => entry.month === currentMonthStr);
      if (foundMonth) {
        setSelectedMonth(currentMonthStr);
      }
  
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [token]);

  const months = attendanceData?.monthly_attendance?.map((entry) => entry.month) || [];

  const selectedMonthData = attendanceData?.monthly_attendance?.find(
    (entry) => entry.month === selectedMonth
  );

  const currentMonthAttendance = selectedMonthData?.attendance || [];

  const indicators = {
    A: currentMonthAttendance.filter((record) => record.da_type === "A").length,
    P: currentMonthAttendance.filter((record) => record.da_type === "P").length,
    WO: currentMonthAttendance.filter((record) => record.da_type === "WO").length,
  };

  const handleExportToExcel = () => {
    if (!selectedMonthData?.attendance?.length) {
      alert("No data to export!");
      return;
    }
  
    const dataToExport = selectedMonthData.attendance.map((record) => ({
      Date: record.date_with_day,
      "In Time": formatTo12Hour(record.in_time),
      "Out Time": formatTo12Hour(record.out_time),
      "Total Hours": record.total_hours,
      "DA Type": record.da_type,
      Portion: record.portion,
    }));
  
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, selectedMonth);
  
    XLSX.writeFile(workbook, `Attendance_${selectedMonth.replace(/\s/g, '_')}.xlsx`);
  };
  
  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };
  
  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="ms-1 text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 md:ms-2 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg
                className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">
                My Attendance
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
          My Attendance
        </h2>
      </div>

      {/* Employee Info Cards */}
      {selectedMonthData?.attendance.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Employee Card */}
          <div className="bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-2xl border border-blue-100 dark:border-blue-800/30">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg text-blue-600 dark:text-blue-400">
                <User className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Employee
              </span>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white truncate font-poppins">
                {user?.name || "N/A"}
              </h4>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5 font-poppins font-semibold">
                <UserCheck className="w-3.5 h-3.5" /> {employeeId || "N/A"}
              </p>
            </div>
          </div>

          {/* Month Filter Card */}
          <div className="bg-emerald-50/50 dark:bg-emerald-900/10 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-800/30">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg text-emerald-600 dark:text-emerald-400">
                <Calendar className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Filter by Month
              </span>
            </div>
            <div className="relative">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full bg-white dark:bg-gray-800 border-2 border-emerald-100 dark:border-emerald-800/50 text-gray-800 dark:text-white rounded-xl px-4 py-2.5 pr-10 font-medium appearance-none focus:outline-none focus:border-emerald-500 transition-all cursor-pointer"
              >
                {months.map((month, idx) => (
                  <option key={idx} value={month}>
                    {month}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-600 dark:text-emerald-400 pointer-events-none" />
            </div>
          </div>
        </div>
      )}

      {/* Indicators + Action Buttons */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        {/* Indicators */}
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2.5 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 px-4 py-2.5 rounded-xl shadow-sm">
            <div className="p-1.5 bg-red-100 dark:bg-red-800/50 rounded-lg">
              <UserX className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
            <span className="text-sm font-semibold text-gray-700 dark:text-white">
              Absent: <span className="text-red-600 dark:text-red-400">{indicators.A}</span>
            </span>
          </div>
          <div className="flex items-center gap-2.5 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/30 px-4 py-2.5 rounded-xl shadow-sm">
            <div className="p-1.5 bg-green-100 dark:bg-green-800/50 rounded-lg">
              <UserCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-sm font-semibold text-gray-700 dark:text-white">
              Present: <span className="text-green-600 dark:text-green-400">{indicators.P}</span>
            </span>
          </div>
          <div className="flex items-center gap-2.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800/30 px-4 py-2.5 rounded-xl shadow-sm">
            <div className="p-1.5 bg-yellow-100 dark:bg-yellow-800/50 rounded-lg">
              <Coffee className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            </div>
            <span className="text-sm font-semibold text-gray-700 dark:text-white">
              Week Off: <span className="text-yellow-600 dark:text-yellow-400">{indicators.WO}</span>
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {selectedMonthData?.attendance.length > 0 && (
            <button
              onClick={() => setSelectedMonth(defaultMonth)}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium shadow-lg shadow-blue-200 dark:shadow-none transition-all hover:translate-y-[-2px] active:translate-y-[0]"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          )}
          {selectedMonthData?.attendance?.length > 0 && (
            <button
              onClick={handleExportToExcel}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium shadow-lg shadow-emerald-200 dark:shadow-none transition-all hover:translate-y-[-2px] active:translate-y-[0]"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export to Excel
            </button>
          )}
        </div>
      </div>

      {/* Attendance Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                Sr No.
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Day
                </div>
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  In Time
                </div>
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  Out Time
                </div>
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                Total Hrs
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                Status
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                Portion
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-8">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">Loading attendance...</p>
                  </div>
                </td>
              </tr>
            ) : selectedMonthData?.attendance && selectedMonthData?.attendance.length > 0 ? (
              selectedMonthData?.attendance.map((record, idx) => (
                <tr 
                  key={idx} 
                  className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white font-medium">
                    {idx + 1}
                  </td>
                  <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white font-medium">
                    {record.date_with_day}
                  </td>
                  <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white">
                    {formatTo12Hour(record.in_time)}
                  </td>
                  <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white">
                    {formatTo12Hour(record.out_time)}
                  </td>
                  <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white font-semibold">
                    {record.total_hours}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <StatusBadge 
                      status={record.da_type === "A" ? "Absent" : record.da_type === "P" ? "Present" : "Week Off"} 
                    />
                  </td>
                  <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white">
                    {record.portion || "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-8">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                      No attendance data available for the selected month.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendancePage;
